/**
 * Where a node/trigger config form is being hosted, which decides its chrome:
 *
 * - `panel` — the chat artifact panel. It supplies its own close button, so the
 *   form must not draw a second one.
 * - `page`  — a full-width stack view. Centred column, footer pinned to the
 *   bottom of the page rather than a panel.
 * - `inspector` — floating over the workflow canvas. Nothing else can dismiss it,
 *   so the form owns the close affordance.
 */
export type WorkflowEditLayout = 'panel' | 'page' | 'inspector';

/** Which edge of the canvas the floating inspector docks to. */
export type WorkflowInspectorPlacement = 'side' | 'bottom';

/** Matches the inspector's `-3` insets from the canvas edges. */
export const WORKFLOW_INSPECTOR_GUTTER = 12;

export const WORKFLOW_INSPECTOR_WIDTH = 380;

/** Bottom-docked height. Capped against short canvases by a `max-h` on the panel. */
export const WORKFLOW_INSPECTOR_HEIGHT = 416;

/**
 * Canvas left over beside the inspector before side-docking stops being worth it.
 * A node card is 300px, so anything under this can't show a whole card with
 * context either side — which is the entire point of keeping the canvas visible.
 */
const MIN_CANVAS_BESIDE_INSPECTOR = 480;

/**
 * Side or bottom, from how much room the canvas actually has.
 *
 * Which dimension the inspector can afford to spend flips with the room. With
 * chat alongside, the artifact panel is around 600px — horizontal is the scarce
 * dimension there, since a 380px panel would leave less canvas than one node card
 * is wide. So it docks to the bottom, spanning the full width and spending height
 * instead. Given the whole body, horizontal is abundant and the graph runs
 * top-to-bottom, so the side is the cheap edge to give up.
 *
 * Measured rather than inferred from who is hosting the canvas. This used to
 * bottom-dock unless the artifact panel reported itself expanded, which stood in
 * for "there is room" until a second way to fill the window arrived: a workflow
 * opened as its own page is as wide as the canvas ever gets and expands nothing,
 * so the inspector kept docking to the bottom of the roomiest view in the app.
 * The width was always the real question, and it is the one asked here.
 */
export function workflowInspectorPlacement(
  canvasWidth: number
): WorkflowInspectorPlacement {
  return canvasWidth >= WORKFLOW_INSPECTOR_WIDTH + MIN_CANVAS_BESIDE_INSPECTOR
    ? 'side'
    : 'bottom';
}
